<template>
  <a-spin :spinning="isLoading">
    <div class="min-h-full flex items-center justify-center p-6">
      <div class="relative w-96">
        <transition
            mode="out-in"
            enter-active-class="animate__animated animate__fadeIn animate__fast"
            leave-active-class="zoomout-leave-active"
        >
          <ScreenError v-if="showError" />
          <ScreenPassword
              v-else-if="showPassword"
              :is-loading="isLoadingPassword"
              @submit="fetchSurvey"
          />
          <CardStack
              v-else
              :cards="cards"
              :current-index="currentIndex"
              :progress="questionProgress"
              :show-nav="survey.showNavigation"
              :show-progress="survey.showProgress"
              @next="onNext"
              @prev="onPrev"
          />
        </transition>
        <transition enter-active-class="animate__animated animate__pulse animate__fast">
          <ScreenThankyou
              v-if="showThankyou"
              :thankyou-id="thankyouId"
          />
        </transition>
      </div>
    </div>
  </a-spin>
</template>

<script>
import {cloneDeep} from "lodash";
import QuestionComment from "./Questions/QuestionComment";
import ScreenWelcome from "./Screens/ScreenWelcome";
import ScreenThankyou from "./Screens/ScreenThankyou";
import QuestionSwipe from "./Questions/QuestionSwipe";
import QuestionMultiple from "./Questions/QuestionMultiple";
import QuestionNps from "./Questions/QuestionNps";
import QuestionRating from "./Questions/QuestionRating";
import QuestionOpen from "./Questions/QuestionOpen";
import ScreenError from "./Screens/ScreenError";
import ScreenPassword from "./Screens/ScreenPassword";
import CardStack from "./CardStack";
import {SUBMIT_SURVEY, FETCH_SURVEY} from "../store/actions.type";
import {mapGetters} from "vuex";
import {Spin} from 'ant-design-vue';
import {
  isIframe,
  isPreviewMode,
  isCreatorMode,
  postMessageTypes,
  listUrlParamsStr,
  setBackground,
} from "../helpers/fns";
import {calculate, evaluateCondition} from "../helpers/questionLogic";
import themeMixin from "../mixins/theme.mixin";
import {SET_SURVEY} from "../store/mutations.type";

export default {
  name: "TheSurvey",
  components: {
    'a-spin': Spin,
    ScreenThankyou,
    ScreenError,
    ScreenPassword,
    CardStack
  },
  mixins: [themeMixin],
  mounted() {
    if (!isCreatorMode) {
      this.fetchSurvey()
    }

    window.addEventListener('beforeunload', this.onWindowClose)
    window.addEventListener("message", this.onMessage)
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.onWindowClose)
    window.removeEventListener("message", this.onMessage)
  },
  data() {
    return {
      questionComponents: {
        swipe: QuestionSwipe,
        multiple: QuestionMultiple,
        nps: QuestionNps,
        rating: QuestionRating,
        singleLine: QuestionOpen,
        multiLine: QuestionOpen,
        email: QuestionOpen,
        phone: QuestionOpen,
        number: QuestionOpen,
      },
      currentIndex: 0,
      cardIndexHistory: [],
      calculatedFieldsHistory: [],
      answers: {},
      showError: false,
      showPassword: false,
      showThankyou: false,
      thankyouId: null,
      isLoading: false,
      isLoadingPassword: false,
    }
  },
  computed: {
    ...mapGetters([
      "survey",
      "questions",
      "needPassword",
    ]),
    cards() {
      const cards = []

      if (this.showError || this.showPassword || this.showThankyou) return cards;

      if (this.survey.welcomeScreen?.isActive) {
        cards.push({type: 'welcome', component: ScreenWelcome})
      }

      this.questions.forEach((question) => {
        cards.push({
          type: 'question',
          component: this.questionComponents[question.type],
          question,
          required: question.required,
          imgSrc: question.imageSrc,
        })
      })

      if (this.survey.comment?.active) {
        cards.push({type: 'comment', component: QuestionComment, required: false})
      }

      return cards
    },
    currentQuestionIndex() {
      const questionCards = this.cards.filter(card => card.type === 'question');
      const currentCard = this.cards[this.currentIndex];
      return questionCards.indexOf(currentCard);
    },
    questionProgress() {
      const progressCards = this.cards.filter(card =>
          card.type === 'question' || card.type === 'comment'
      );
      const currentCard = this.cards[this.currentIndex];
      const currentProgressIndex = progressCards.indexOf(currentCard);
      if (currentProgressIndex > -1 && progressCards.length > 1) {
        return Math.round((currentProgressIndex / (progressCards.length - 1)) * 100);
      }
      return 0;
    },
  },
  methods: {
    async fetchSurvey(password = null) {
      try {
        this.isLoading = true

        if (password) {
          this.isLoadingPassword = true;
        }

        await this.$store.dispatch(FETCH_SURVEY, {
          surveyId: this.$route.params.surveyId,
          password
        });

        this.showPassword = false;
        this.showError = false;
        document.title = this.survey.title || "Survey";
      } catch {
        this.showPassword = this.needPassword;
        this.showError = !this.needPassword;
      } finally {
        this.isLoading = false;
        this.isLoadingPassword = false;
        setBackground(this.bgColor?.backgroundColor);
      }
    },
    onNext(data) {
      // Save answer
      if (data?.question_id) {
        this.$set(this.answers, data.question_id, data);
      }

      // Save history
      this.cardIndexHistory.push(this.currentIndex);
      this.calculatedFieldsHistory.push(cloneDeep(this.survey.calculatedFields));

      // Helpers for calculated fields
      const getCalculatedField = (id) => this.survey.calculatedFields.find(field => field.id === id);
      const setCalculatedFieldValue = (id, value) => {
        const field = getCalculatedField(id);
        if (field) field.value = value;
      }

      // Logic
      const logics = this.questions[this.currentQuestionIndex]?.logics;
      if (logics && logics.length) {
        for (const logic of logics.filter(logic => logic.active)) {
          // Evaluate conditions with "and" or "or" operators
          const hasTrueCondition = logic.conditions.reduce((result, condition, index) => {
            const value = condition.type === "answer" ? data?.value : getCalculatedField(condition.type)?.value;
            if (index === 0) {
              return evaluateCondition(condition.trigger, value, condition.value);
            } else {
              if (condition.operator === "and") {
                return result && evaluateCondition(condition.trigger, value, condition.value);
              } else if (condition.operator === "or") {
                return result || evaluateCondition(condition.trigger, value, condition.value);
              } else {
                return result; // Handle invalid operator cases
              }
            }
          }, false);

          // If logic conditions are met, perform actions
          if (hasTrueCondition) {
            let gotoHandled = false;
            for (const action of logic.actions) {
              if (action.type === "goto" && !gotoHandled) {
                const questionId = action.value;

                if (questionId === "lastStepComment") {
                  this.currentIndex = this.cards.length - 1;
                } else if (questionId?.includes("thankyou")) {
                  this.thankyouId = questionId.split("-")[1];
                  this.onFinish("");
                } else if (questionId) {
                  const nextIndex = this.cards.findIndex(card => card?.question?._id === questionId);
                  if (nextIndex !== -1) {
                    this.currentIndex = nextIndex;
                  } else {
                    this.currentIndex++;
                  }
                }

                gotoHandled = true;
              } else if (action.type === "calculate") {
                const calculatedFieldObj = getCalculatedField(action.calculatedField);
                const calculatedValue = calculate(calculatedFieldObj?.value, calculatedFieldObj?.type, action.operation, action.value);
                setCalculatedFieldValue(action.calculatedField, calculatedValue);
              }
            }

            // Stop after first goto
            if (gotoHandled) return;
          }
        }
      }

      // If last card, finish survey
      if (this.currentIndex >= this.cards.length - 1) {
        this.onFinish(data?.comment);
        return;
      }

      // Otherwise, go to next card
      this.currentIndex++;
    },
    onPrev() {
      if (this.currentIndex <= 0) return;

      this.currentIndex = this.cardIndexHistory.pop();

      if (this.calculatedFieldsHistory.length > 0) {
        this.survey.calculatedFields = this.calculatedFieldsHistory.pop();
      }
    },
    onFinish(comment = '') {
      this.showThankyou = true;

      this.$store.dispatch(SUBMIT_SURVEY, {
        surveyId: String(this.survey._id),
        responses: Object.values(this.answers),
        isFinished: true,
        comment,
        urlParamsStr: listUrlParamsStr(),
        calculatedFields: this.survey.calculatedFields,
      });
    },
    onWindowClose(e) {
      if (
          !isPreviewMode &&
          !isCreatorMode &&
          !isIframe &&
          !this.showThankyou &&
          Object.keys(this.answers).length > 0
      ) {
        e.preventDefault();
        e.returnValue = 'Are you sure? Your answers will be lost.';
        return false;
      }
    },
    onMessage(event) {
      if (event?.origin !== process.env.VUE_APP_ORIGIN_APP) return

      const {type, data} = event.data

      const setCurrent = (curData) => {
        this.showThankyou = false;
        this.showPassword = false;

        if (curData?.type === "password") {
          this.showPassword = true;
        } else if (curData?.type === "welcome") {
          this.currentIndex = 0;
        } else if (curData?.type === "thankyou") {
          this.showThankyou = true;
          this.thankyouId = curData?.currentId || null;
        } else if (curData?.type === "comment") {
          this.currentIndex = this.cards.length - 1;
        } else if (curData?.currentId) {
          const idx = this.cards.findIndex(card => card.question?._id === curData.currentId);
          if (idx !== -1) {
            this.currentIndex = idx;
          }
        }
      }

      switch (type) {
        case postMessageTypes.setSurvey:
          this.$store.commit(SET_SURVEY, data?.survey || {})
          setCurrent(data?.current)
          setBackground(this.bgColor?.backgroundColor)
          break;
        case postMessageTypes.setCurrent:
          setCurrent(data)
          break;
      }
    }
  }
}
</script>

<style lang="less" scoped>
.zoomout-leave-active {
  animation: zoom-out 0.3s cubic-bezier(0.4, 0, 1, 1) forwards;
}

@keyframes zoom-out {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  60% {
    transform: scale(1.05);
    opacity: 0.8;
  }
  80% {
    transform: scale(1.05);
    opacity: 0.4;
  }
  100% {
    transform: scale(1.05);
    opacity: 0;
  }
}
</style>
